import {
  isRetryableUploadError,
  newClientUploadId,
  uploadEvaluationVideo,
  uploadEvaluationVideoAudio,
  uploadKotSegmentAudio,
} from "../api/evaluationVideoUploadApi";
import { createLocalClipStore } from "./championshipLocalClips";

/**
 * Evaluation / KoT uploads that could not reach the server wait here, in
 * order, like championship clips do. A KoT finalize never runs ahead of
 * its own pieces because anything new joins the back of the line while
 * older rows are still waiting.
 */

export const EVAL_BACKLOG_FLUSH_MS = 45_000;

let store = null;
function backlogStore() {
  if (!store) store = createLocalClipStore("smarttt-eval-clips");
  return store;
}

let waitingCount = null;

export async function evalUploadBacklogCount() {
  if (waitingCount == null) {
    try {
      waitingCount = await backlogStore().count();
    } catch {
      waitingCount = 0;
    }
  }
  return waitingCount;
}

async function runRow(row, { retry }) {
  const { kind, args = {}, blob = null, audioBlob = null } = row;
  if (kind === "kot_segment") {
    const result = await uploadEvaluationVideo({ ...args, blob, retry });
    if (audioBlob) {
      await uploadKotSegmentAudio({
        kotMatchId: args.kotMatchId,
        segmentIndex: args.segmentIndex,
        blob: audioBlob,
        retry,
      });
    }
    return result;
  }
  if (kind === "kot_finalize") {
    return uploadEvaluationVideo({ ...args, blob: null, retry });
  }
  const result = await uploadEvaluationVideo({ ...args, blob, retry });
  const messageId = result?.data?.message_id;
  // A slot-dedupe hit is another recording; only mux our own (or a replay of it).
  const ours = !result?.already_exists || result?.idempotent_replay;
  if (audioBlob && messageId && ours) {
    void uploadEvaluationVideoAudio(messageId, audioBlob).catch((err) =>
      console.warn("Evaluation audio upload failed:", err?.message || err)
    );
  }
  return result;
}

async function keepOnPhone(kind, { args, blob, audioBlob }) {
  const row = {
    id: `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    kind,
    args,
    blob: blob || null,
    audioBlob: audioBlob || null,
    mime: blob?.type || "",
    byteSize: blob?.size || 0,
    createdAt: Date.now(),
  };
  await backlogStore().save(row);
  waitingCount = (waitingCount || 0) + 1;
  return { saved_on_phone: true };
}

/**
 * Upload now, or keep on the phone when the network / server is down
 * (or older uploads are still waiting). Rejected uploads still throw.
 */
export async function runEvalUpload(kind, input) {
  const payload =
    kind === "kot_segment" || input?.args?.clientUploadId
      ? input
      : { ...input, args: { ...input?.args, clientUploadId: newClientUploadId() } };
  if ((await evalUploadBacklogCount()) > 0) {
    try {
      return await keepOnPhone(kind, payload);
    } catch (err) {
      console.warn("Could not keep clip on phone:", err?.message || err);
    }
  }
  try {
    return await runRow({ kind, ...payload }, { retry: true });
  } catch (err) {
    if (!isRetryableUploadError(err)) throw err;
    try {
      return await keepOnPhone(kind, payload);
    } catch (storeErr) {
      console.warn("Could not keep clip on phone:", storeErr?.message || storeErr);
      throw err;
    }
  }
}

/** Send waiting rows oldest first; stop at the first network/server failure. */
export async function flushEvalUploadBacklog() {
  if (!(await evalUploadBacklogCount())) return { sent: 0, waiting: 0 };
  let rows;
  try {
    rows = await backlogStore().list();
  } catch {
    return { sent: 0, waiting: waitingCount || 0 };
  }
  let sent = 0;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    try {
      await runRow(row, { retry: false });
      sent += 1;
    } catch (err) {
      if (isRetryableUploadError(err)) {
        waitingCount = rows.length - i;
        return { sent, waiting: waitingCount };
      }
      console.warn(`Dropping rejected ${row.kind} upload:`, err?.message || err);
    }
    await backlogStore().remove(row.id).catch(() => {});
  }
  waitingCount = 0;
  return { sent, waiting: 0 };
}

let flusherUsers = 0;
let flusherStop = null;

/**
 * Keep retrying the backlog while any recorder is mounted. `enqueue` is the
 * serial clip queue, so replays never interleave with live uploads.
 */
export function startEvalUploadBacklogFlusher(enqueue) {
  flusherUsers += 1;
  if (!flusherStop) {
    let busy = false;
    const tick = () => {
      if (busy) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      busy = true;
      enqueue(async () => {
        try {
          await flushEvalUploadBacklog();
        } catch {
          /* next tick retries */
        } finally {
          busy = false;
        }
      });
    };
    const timer = setInterval(tick, EVAL_BACKLOG_FLUSH_MS);
    window.addEventListener("online", tick);
    tick();
    flusherStop = () => {
      clearInterval(timer);
      window.removeEventListener("online", tick);
    };
  }
  return () => {
    flusherUsers -= 1;
    if (flusherUsers <= 0 && flusherStop) {
      flusherStop();
      flusherStop = null;
      flusherUsers = 0;
    }
  };
}

export function resetEvalUploadBacklogForTests() {
  waitingCount = null;
  store = null;
}
